import React, { useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import {
  AdvanceTable,
  AdvanceTableWrapper,
  AdvanceTableHeader,
  AdvanceTablePagination
} from 'components/common/advance-table';
import { TABLE_COLUMN_FILTER_KEY, ROLES, DRIVER } from 'constants/index';
import { getTablePageSize } from 'helpers/tableSettings';
import useGetTableColumns from './services/useGetTableColumns';
import useFetchDriver from './services/useFetchDriver';
import useFetchPaymentVelocity from './services/useFetchPaymentVelocity';
import AddUpdateDriver from './AddUpdateDriver';
import ScreenSearchPanel from 'components/common/search/ScreenSearchPanel';
import { useAuth } from 'hooks/useAuth';
import { DriversContext } from './DriversContext';

const Drivers = () => {
  const { viewAsRole, userSettings } = useAuth();
  const [tablePageIndex, setTablePageIndex] = useState(0);
  const { availableColumns, visibleColumnIds, defaultVisibleColumnIds } = useMemo(
    () => useGetTableColumns(TABLE_COLUMN_FILTER_KEY.DRIVER),
    [TABLE_COLUMN_FILTER_KEY.DRIVER]
  );
  const [isShowModal, setIsShowModal] = useState({ show: false, driverId: null });
  const {
    loading,
    driverList,
    driverDetail,
    fetchDriverList,
    fetchDriverDetail,
    resetDriverDetail,
    loadingDetail
  } = useFetchDriver();
  // Held here rather than inside the fraud tab so the lock-source badge in the
  // driver header and the tab itself share one fetch and one state.
  const paymentVelocity = useFetchPaymentVelocity();
  const { driverId, show } = isShowModal;
  const { items = [], total_count: totalCount = 0 } = driverList || {};

  // Only portal admin holds the payment-velocity permission on the API, so any
  // other role would just collect 403s.
  const canViewPaymentVelocity = viewAsRole === ROLES.ADMIN;

  const onAddNew = () => {
    setIsShowModal({ driverId: null, show: true });
  };

  const onClickRow = row => {
    const { id, fleet_id, company_id } = row;
    fetchDriverDetail({
      driver_id: id,
      fleet_id,
      company_id
    });

    if (canViewPaymentVelocity) {
      paymentVelocity.refresh(id);
    }

    setIsShowModal({
      driverId: id,
      show: true
    });
  };

  const onCloseModal = () => {
    resetDriverDetail();
    setIsShowModal({ driverId: null, show: false });
  };

  const fetchDriverListByPage = () => {
    fetchDriverList({
      pageIndex: tablePageIndex
    });
  };

  const onUpdateStatusSuccess = driverInfo => {
    fetchDriverListByPage();
    fetchDriverDetail(driverInfo);
  };

  const onUpdateOrCreateDriverSuccess = () => {
    onCloseModal();
    fetchDriverListByPage();
  };

  const onChangePage = page => {
    setTablePageIndex(page);
  };

  const providerValues = useMemo(
    () => ({
      driverId,
      loadingDetail,
      onCloseModal,
      fetchDriverDetail,
      onUpdateStatusSuccess,
      onUpdateOrCreateDriverSuccess,
      paymentVelocity,
      canViewPaymentVelocity,
      data: driverId ? driverDetail : {}
    }),
    [
      driverId,
      driverDetail,
      onCloseModal,
      loadingDetail,
      fetchDriverDetail,
      paymentVelocity,
      canViewPaymentVelocity,
      onUpdateOrCreateDriverSuccess,
      onUpdateStatusSuccess
    ]
  );

  return (
    <DriversContext.Provider value={providerValues}>
      <AdvanceTableWrapper
        sortable
        pagination
        data={items}
        loading={loading}
        perPage={getTablePageSize(userSettings, TABLE_COLUMN_FILTER_KEY.DRIVER)}
        totalCount={totalCount}
        rowsPerPageSelection
        onChangePage={onChangePage}
        columns={availableColumns}
        fetchData={fetchDriverList}
        visibleColumnIds={visibleColumnIds}
        defaultVisibleColumnIds={defaultVisibleColumnIds}
      >
        <Card className="mb-3">
          <Card.Header>
            <AdvanceTableHeader
              table
              canAddNew
              title="Drivers"
              canFilter
              onAddNew={onAddNew}
              hasPermissionAdd={ROLES.DRIVER !== viewAsRole}
              canFilterColumns
              filterKey={TABLE_COLUMN_FILTER_KEY.DRIVER}
              persistPageSize
            />
          </Card.Header>
          <Card.Body className="p-0 custom-table d-flex flex-wrap pb-md-3">
            <div className="flex-1 d-flex flex-column flex-1 scrollbar visible-scrollbar h-fit-content w-100 rounded-end-lg">
              <AdvanceTable
                table
                onClickRow={onClickRow}
                headerClassName="bg-200 text-nowrap"
                rowClassName="align-middle white-space-nowrap cursor-pointer"
                tableProps={{
                  size: 'sm',
                  striped: true,
                  className: 'fs--1 mb-0 overflow-hidden'
                }}
              />
              <Card.Footer>
                <AdvanceTablePagination table />
              </Card.Footer>
            </div>
            <ScreenSearchPanel table pageKey={DRIVER} />
          </Card.Body>
        </Card>
      </AdvanceTableWrapper>
      {show && (
        <AddUpdateDriver
          show={show}
          driverId={driverId}
          onCloseModal={onCloseModal}
          data={driverId ? driverDetail : {}}
        />
      )}
    </DriversContext.Provider>
  );
};

export default Drivers;
